import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "@iconify/react";
import downloadIcon from "@iconify/icons-solar/download-linear";
import refreshIcon from "@iconify/icons-solar/refresh-linear";
import arrowIcon from "@iconify/icons-solar/arrow-right-linear";
import TopBar from "../components/layout/TopBar";
import { useToast } from "../context/ToastContext";
import { api } from "../lib/api";
import {
  Report,
  Totals,
  fmt,
  shortMoney,
  monthName,
  dateLabel,
  changeLabel,
  reportCSV,
  stageLabels,
} from "../components/reports/reportData";
import "./Reports.css";

const metrics: { key: keyof Totals; label: string; note: string }[] = [
  {
    key: "saleValue",
    label: "Deal value",
    note: "Includes open deals and inquiries",
  },
  {
    key: "recordedReceipts",
    label: "Recorded receipts",
    note: "Received to date on selected deals",
  },
  {
    key: "commission",
    label: "Recorded commission",
    note: "Assigned amounts, not payouts",
  },
  { key: "deals", label: "Deals created", note: "Cancelled deals excluded" },
];
function Trend({ data, compare }: { data: Report; compare: boolean }) {
  const max = Math.max(
    1,
    ...data.months.map((m) => m.saleValue),
    ...(compare ? data.previous.months.map((m) => m.saleValue) : []),
  );
  const step = 690 / data.months.length;
  const barWidth = Math.min(32, step * 0.3);
  return (
    <div
      className="rp-chart-scroll"
      role="region"
      aria-label="Monthly deal value chart; exact values in the monthly table below"
      tabIndex={0}
    >
      <svg
        viewBox="0 0 800 256"
        role="img"
        aria-labelledby="rp-chart-title rp-chart-description"
      >
        <title id="rp-chart-title">
          Monthly deal value in {data.period.year}
        </title>
        <desc id="rp-chart-description">
          Deals grouped by creation month.{" "}
          {compare ? `Compared with ${data.period.year - 1}.` : ""} Expand
          monthly figures for exact amounts.
        </desc>
        {[0, 0.25, 0.5, 0.75, 1].map((f) => (
          <g key={f}>
            <line
              x1="86"
              x2="778"
              y1={208 - f * 172}
              y2={208 - f * 172}
              className="rp-grid-line"
            />
            <text x="74" y={212 - f * 172} textAnchor="end">
              {shortMoney(max * f)}
            </text>
          </g>
        ))}
        {data.months.map((m, i) => {
          const x = 86 + step * (i + 0.5);
          const previous =
            data.previous.months.find((p) => p.month === m.month)?.saleValue ||
            0;
          return (
            <g key={m.key}>
              {compare && (
                <rect
                  x={x - barWidth - 2}
                  y={208 - (previous / max) * 172}
                  width={barWidth}
                  height={(previous / max) * 172}
                  rx="2"
                  className="rp-bar-previous"
                >
                  <title>
                    {monthName(m.month)} {data.period.year - 1}: {fmt(previous)}
                  </title>
                </rect>
              )}
              <rect
                x={compare ? x + 2 : x - barWidth / 2}
                y={208 - (m.saleValue / max) * 172}
                width={barWidth}
                height={(m.saleValue / max) * 172}
                rx="2"
                className="rp-bar"
              >
                <title>
                  {monthName(m.month)} {data.period.year}: {fmt(m.saleValue)}
                </title>
              </rect>
              <text x={x} y="234" textAnchor="middle">
                {monthName(m.month)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
export default function Reports() {
  const currentYear = Number(
    new Intl.DateTimeFormat("en", {
      year: "numeric",
      timeZone: "Asia/Manila",
    }).format(new Date()),
  );
  const [year, setYear] = useState(String(currentYear));
  const [period, setPeriod] = useState("ytd");
  const [compare, setCompare] = useState(false);
  const [data, setData] = useState<Report | null>(null);
  const [years, setYears] = useState([currentYear]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refresh, setRefresh] = useState(0);
  const toast = useToast();
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    api.reports
      .summary({ year, period })
      .then((result: Report) => {
        if (active) {
          setData(result);
          setYears(result.years);
        }
      })
      .catch((err: Error) => {
        if (active) {
          setError(err.message || "Please try again.");
          setData(null);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [year, period, refresh]);
  const exportReport = () => {
    if (!data || loading) return;
    try {
      const url = URL.createObjectURL(
        new Blob([reportCSV(data)], { type: "text/csv;charset=utf-8;" }),
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = `vop-report-${data.period.start}-to-${data.period.end}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Report export ready");
    } catch {
      toast.error("Could not export the report. Please try again.");
    }
  };
  return (
    <>
      <TopBar title="Reports" showSearch={false} />
      <div className="reports-workspace">
        <header className="rp-heading">
          <div>
            <h2>Business performance</h2>
            <p>
              Review deal activity, recorded amounts, and the accounts that need
              attention.
            </p>
          </div>
          <button
            className="rp-button"
            onClick={exportReport}
            disabled={loading || !data}
          >
            <Icon icon={downloadIcon} width={18} />
            Export report
          </button>
        </header>
        <div className="rp-toolbar">
          <label>
            Year
            <select value={year} onChange={(e) => setYear(e.target.value)}>
              {Array.from(new Set([...years, Number(year)]))
                .sort((a, b) => b - a)
                .map((y) => (
                  <option key={y}>{y}</option>
                ))}
            </select>
          </label>
          <label>
            Reporting period
            <select value={period} onChange={(e) => setPeriod(e.target.value)}>
              <option value="ytd">Year to date</option>
              <option value="year">Full year</option>
              <option value="q1">Q1 · Jan–Mar</option>
              <option value="q2">Q2 · Apr–Jun</option>
              <option value="q3">Q3 · Jul–Sep</option>
              <option value="q4">Q4 · Oct–Dec</option>
            </select>
          </label>
          <label className="rp-compare">
            <input
              type="checkbox"
              aria-label="Compare with prior year"
              checked={compare}
              onChange={(e) => setCompare(e.target.checked)}
            />
            Compare prior year
          </label>
          <button
            className="rp-button rp-refresh"
            disabled={loading}
            onClick={() => setRefresh((v) => v + 1)}
          >
            <Icon icon={refreshIcon} width={17} />
            Refresh
          </button>
        </div>
        {loading ? (
          <div className="rp-state" role="status">
            <h3>Loading report…</h3>
            <p>Gathering results for the selected period.</p>
          </div>
        ) : error || !data ? (
          <div className="rp-state" role="alert">
            <h3>Report unavailable</h3>
            <p>{error}</p>
            <button
              className="rp-button"
              onClick={() => setRefresh((v) => v + 1)}
            >
              Try again
            </button>
          </div>
        ) : (
          <>
            <div className="rp-period" aria-live="polite">
              <strong>
                {dateLabel(data.period.start)} – {dateLabel(data.period.end)}
              </strong>
              <span>Deals grouped by creation date · Philippine time</span>
            </div>
            <dl className="rp-metrics">
              {metrics.map((m) => (
                <div key={m.key}>
                  <dt>{m.label}</dt>
                  <dd>
                    {m.key === "deals"
                      ? data.totals[m.key]
                      : fmt(data.totals[m.key])}
                  </dd>
                  <small>{m.note}</small>
                  {compare && (
                    <span className="rp-change">
                      {changeLabel(
                        data.totals[m.key],
                        data.previous.totals[m.key],
                      )}
                    </span>
                  )}
                </div>
              ))}
            </dl>
            {data.accountsToReview > 0 && (
              <aside className="rp-warning">
                <div>
                  <strong>
                    {data.accountsToReview}{" "}
                    {data.accountsToReview === 1
                      ? "account needs"
                      : "accounts need"}{" "}
                    reconciliation
                  </strong>
                  <p>
                    Recorded receipts differ from payment records or schedule
                    totals. Review these accounts before relying on collection
                    figures.
                  </p>
                </div>
                <Link to="/payments">
                  Review payments <Icon icon={arrowIcon} width={16} />
                </Link>
              </aside>
            )}
            {data.totals.deals === 0 && (
              <div className="rp-empty-period" role="status">
                <strong>No deals created in this period</strong>
                <span>
                  Choose another period to review activity. Current inventory
                  and overdue schedules are still shown below.
                </span>
              </div>
            )}
            <section className="rp-panel rp-trend">
              <header className="rp-section-header">
                <div>
                  <h3>Monthly deal value</h3>
                  <p>Value of non-cancelled deals by creation month.</p>
                </div>
                <div className="rp-legend">
                  <span>
                    <i />
                    {data.period.year}
                  </span>
                  {compare && (
                    <span>
                      <i className="previous" />
                      {data.period.year - 1}
                    </span>
                  )}
                </div>
              </header>
              {compare && (
                <p className="rp-comparison-note">
                  Comparison: {dateLabel(data.period.previousStart)} –{" "}
                  {dateLabel(data.period.previousEnd)}. Each cohort shows its
                  current recorded amounts.
                </p>
              )}
              {data.totals.deals || (compare && data.previous.totals.deals) ? (
                <Trend data={data} compare={compare} />
              ) : (
                <p className="rp-chart-empty">
                  No deal value to chart for this period.
                </p>
              )}
              <details className="rp-monthly">
                <summary>
                  View monthly figures <span>{data.months.length} months</span>
                </summary>
                <div
                  className="rp-table-scroll"
                  role="region"
                  aria-label="Monthly figures"
                  tabIndex={0}
                >
                  <table>
                    <caption className="rp-sr">
                      Monthly totals for selected deals
                    </caption>
                    <thead>
                      <tr>
                        <th scope="col">Month</th>
                        <th scope="col">Deals</th>
                        <th scope="col">Deal value</th>
                        <th scope="col">Receipts to date</th>
                        <th scope="col">Commission</th>
                        {compare && <th scope="col">Prior-year deal value</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {data.months.map((m) => (
                        <tr key={m.key}>
                          <th scope="row">
                            {monthName(m.month)} {data.period.year}
                          </th>
                          <td>{m.deals}</td>
                          <td>{fmt(m.saleValue)}</td>
                          <td>{fmt(m.recordedReceipts)}</td>
                          <td>{fmt(m.commission)}</td>
                          {compare && (
                            <td>
                              {fmt(
                                data.previous.months.find(
                                  (p) => p.month === m.month,
                                )?.saleValue || 0,
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <th scope="row">Total</th>
                        <td>{data.totals.deals}</td>
                        <td>{fmt(data.totals.saleValue)}</td>
                        <td>{fmt(data.totals.recordedReceipts)}</td>
                        <td>{fmt(data.totals.commission)}</td>
                        {compare && (
                          <td>{fmt(data.previous.totals.saleValue)}</td>
                        )}
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </details>
            </section>
            <div className="rp-two-columns">
              <section className="rp-panel">
                <header className="rp-section-header">
                  <div>
                    <h3>Agent results</h3>
                    <p>Selected deals, ranked by deal value.</p>
                  </div>
                </header>
                {data.agents.length ? (
                  <div
                    className="rp-table-scroll"
                    role="region"
                    aria-label="Agent results"
                    tabIndex={0}
                  >
                    <table>
                      <thead>
                        <tr>
                          <th scope="col">Agent</th>
                          <th scope="col">Deals</th>
                          <th scope="col">Deal value</th>
                          <th scope="col">Receipts to date</th>
                          <th scope="col">Commission</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.agents.map((a) => (
                          <tr key={a.id}>
                            <th scope="row">{a.name}</th>
                            <td>{a.deals}</td>
                            <td>{fmt(a.saleValue)}</td>
                            <td>{fmt(a.recordedReceipts)}</td>
                            <td>{fmt(a.commission)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="rp-empty">No agent activity in this period.</p>
                )}
              </section>
              <section className="rp-panel">
                <header className="rp-section-header">
                  <div>
                    <h3>Where these deals stand</h3>
                    <p>Current stage of deals created in this period.</p>
                  </div>
                </header>
                <ul className="rp-stages">
                  {data.stages.map((s) => (
                    <li key={s.stage}>
                      <div>
                        <span>{stageLabels[s.stage] || s.stage}</span>
                        <strong>
                          {s.count}
                          <small>
                            {data.totals.deals
                              ? Math.round((s.count / data.totals.deals) * 100)
                              : 0}
                            %
                          </small>
                        </strong>
                      </div>
                      <div className="rp-track" aria-hidden="true">
                        <span
                          style={{
                            width: `${data.totals.deals ? (s.count / data.totals.deals) * 100 : 0}%`,
                          }}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
                <p className="rp-footnote">
                  Share of selected deals, not a historical conversion funnel.
                </p>
              </section>
            </div>
            <header className="rp-snapshot-heading">
              <div>
                <h3>Current position</h3>
                <p>
                  Live snapshots across all dates. Reporting period filters do
                  not apply.
                </p>
              </div>
              <span>
                As of{" "}
                {new Date(data.generatedAt).toLocaleString("en-PH", {
                  timeZone: "Asia/Manila",
                  month: "short",
                  day: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                })}{" "}
                PHT
              </span>
            </header>
            <div className="rp-two-columns">
              <section className="rp-panel">
                <header className="rp-section-header">
                  <div>
                    <h3>Inventory by section</h3>
                    <p>All lot statuses, including unavailable inventory.</p>
                  </div>
                  <Link to="/inventory">
                    Open map <Icon icon={arrowIcon} width={16} />
                  </Link>
                </header>
                {data.inventory.length ? (
                  <div
                    className="rp-table-scroll"
                    role="region"
                    aria-label="Inventory by section"
                    tabIndex={0}
                  >
                    <table>
                      <thead>
                        <tr>
                          <th scope="col">Section</th>
                          <th scope="col">Available</th>
                          <th scope="col">Reserved</th>
                          <th scope="col">Sold</th>
                          <th scope="col">Other</th>
                          <th scope="col">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.inventory.map((s) => (
                          <tr key={s.id}>
                            <th scope="row">{s.name}</th>
                            <td>{s.available}</td>
                            <td>{s.reserved}</td>
                            <td>{s.sold}</td>
                            <td>{s.other}</td>
                            <td>{s.total}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="rp-empty">No inventory sections yet.</p>
                )}
                <p className="rp-footnote">
                  Other includes maintenance, locked, and blocked lots.
                </p>
              </section>
              <section className="rp-panel">
                <header className="rp-section-header">
                  <div>
                    <h3>Overdue schedules</h3>
                    <p>Unpaid amounts including applied penalties.</p>
                  </div>
                  <Link to="/payments">
                    Open payments <Icon icon={arrowIcon} width={16} />
                  </Link>
                </header>
                <div className="rp-overdue-total">
                  <strong>{fmt(data.overdue.total)}</strong>
                  <span>
                    {data.overdue.count} overdue{" "}
                    {data.overdue.count === 1 ? "schedule" : "schedules"}
                  </span>
                </div>
                {data.overdue.items.length ? (
                  <ul className="rp-overdue">
                    {data.overdue.items.map((p) => (
                      <li key={p.id}>
                        <div>
                          <strong>{p.client}</strong>
                          <span>
                            Lot {p.lot} · {p.daysOverdue} days overdue
                          </span>
                        </div>
                        <b>{fmt(p.amount)}</b>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="rp-empty">
                    No outstanding schedules past their due date.
                  </p>
                )}
                {data.overdue.count > data.overdue.items.length && (
                  <p className="rp-footnote">
                    Showing the {data.overdue.items.length} oldest of{" "}
                    {data.overdue.count} overdue schedules. Total includes all
                    schedules.
                  </p>
                )}
              </section>
            </div>
            <details className="rp-definitions">
              <summary>How to read this report</summary>
              <dl>
                <dt>Deal value</dt>
                <dd>
                  Sum of agreed sale amounts for non-cancelled deals created in
                  the selected period. Includes inquiries and open deals; this
                  is not recognized accounting revenue.
                </dd>
                <dt>Recorded receipts</dt>
                <dd>
                  Current total paid on those deals, including recorded down
                  payments. Later receipts remain attached to the deal’s
                  creation month. This is not a cash-flow report for the
                  selected dates.
                </dd>
                <dt>Recorded commission</dt>
                <dd>
                  Commission saved on each selected deal. Does not imply an
                  approved or paid commission.
                </dd>
                <dt>Prior-year comparison</dt>
                <dd>
                  Same calendar period one year earlier. Year to date uses
                  today’s month and day in both years, adjusted for leap years.
                  Both deal cohorts use current recorded amounts, not historical
                  closing balances.
                </dd>
                <dt>Current position</dt>
                <dd>
                  Inventory counts include all sections. Overdue schedules
                  exclude cancelled sales and cancelled schedules; due today is
                  not overdue. Amounts use outstanding principal plus applied
                  penalties, less recorded payments.
                </dd>
              </dl>
            </details>
          </>
        )}
      </div>
    </>
  );
}
